import { useMemo } from "react";
import clsx from "clsx";
import type { POI, Tag } from "@poi-planner/shared";
import type { MapBounds } from "../hooks/usePoiFilters";
import type { PlannerMapContext } from "../utils/plannerMapContext";
import { mergePlannerMapPois } from "../utils/dayMapPois";
import { MapView } from "./MapView";
import { PaneTitleButton } from "./PaneToggle";

interface TripMapPaneProps {
  plannerContext: PlannerMapContext;
  sidebarPois: POI[];
  tags: Tag[];
  selectedPoiId: string | null;
  flyToTrigger: number;
  onSelectPoi: (poiId: string | null) => void;
  onBoundsChange: (bounds: MapBounds) => void;
  /** Omitted while the Map is the only visible pane. */
  onCollapse?: () => void;
  fitKey: string;
  boundsFilterEnabled: boolean;
  /**
   * Phone layout: the map fills the screen edge to edge under floating
   * controls, so it drops the pane padding and title.
   */
  overlayInsets?: { top: number; bottom: number };
  resultNumberByPoiId?: Map<string, number>;
}

export function TripMapPane({
  plannerContext,
  sidebarPois,
  tags,
  selectedPoiId,
  flyToTrigger,
  onSelectPoi,
  onBoundsChange,
  onCollapse,
  fitKey,
  boundsFilterEnabled,
  overlayInsets,
  resultNumberByPoiId,
}: TripMapPaneProps) {
  const mapPois = useMemo(
    () => mergePlannerMapPois(sidebarPois, plannerContext.pois),
    [plannerContext.pois, sidebarPois],
  );

  return (
    <section
      className={clsx(
        "relative flex min-h-0 min-w-0 flex-1 flex-col bg-subtle",
        !overlayInsets && "p-3",
      )}
      aria-label="Map"
      data-testid="map-pane"
    >
      {!overlayInsets && (
        <div className="absolute left-5 top-5 z-20">
          <PaneTitleButton pane="map" floating onCollapse={onCollapse} />
        </div>
      )}
      <MapView
        pois={mapPois}
        tags={tags}
        selectedPoiId={selectedPoiId}
        flyToTrigger={flyToTrigger}
        onSelectPoi={onSelectPoi}
        onBoundsChange={onBoundsChange}
        routeLines={plannerContext.routeLines}
        routeOrderByPoiId={plannerContext.routeOrderByPoiId}
        fitToPois={boundsFilterEnabled ? undefined : plannerContext.pois}
        fitToPoisKey={fitKey}
        viewportInsets={overlayInsets}
        resultNumberByPoiId={resultNumberByPoiId}
      />
      {plannerContext.unplottableCount > 0 && (
        <div
          className="pointer-events-none absolute bottom-5 left-5 z-20 rounded-md border border-border bg-white/90 px-2.5 py-1 text-xs text-secondary shadow-sm"
          // Runtime geometry: clears the phone results card
          style={
            overlayInsets
              ? { bottom: overlayInsets.bottom + 12, left: 12 }
              : undefined
          }
        >
          {plannerContext.unplottableCount} Planner place
          {plannerContext.unplottableCount === 1 ? "" : "s"} without a location
        </div>
      )}
    </section>
  );
}
